import { count, groupBy } from 'ramda'
import type { DeepReadonly } from 'vue'

import type { Answer } from '../../../answer/models/Answer'
import type { Field } from '../../../question/models/Field'
import {
  assertQuestionType,
  type Question,
  type QuestionType,
} from '../../../question/models/Question'

export const QUESTION_POLICIES = {
  openText: {
    canSubmit: ({ question, answers }) => {
      assertQuestionType(question.type, 'openText')
      const { specialAnswers = [], normalAnswers = [] } = groupAnswers({
        fields: question.fields,
        answers,
      })
      if (hasSelectedChoiceAnswers(specialAnswers)) {
        return true
      }
      return normalAnswers?.every((answer) => {
        if (answer.type !== 'text') {
          throw new Error('No text answer objects found!')
        }
        return answer.value.textInput.length > 0
      })
    },
    shouldClearOnSpecialFieldUpdate: false,
  },
  video: {
    canSubmit: ({ question, answers }) => {
      assertQuestionType(question.type, 'video')
      const { specialAnswers = [], normalAnswers = [] } = groupAnswers({
        fields: question.fields,
        answers,
      })
      if (hasSelectedChoiceAnswers(specialAnswers)) {
        return true
      }

      if (normalAnswers.length !== 1) {
        throw new Error('Video question should only have one normal answer!')
      }

      const [normalAnswer] = normalAnswers
      if (normalAnswer?.type !== 'video') {
        throw new TypeError('Answer is not of type video!')
      }

      return normalAnswer.value.videoBlob !== undefined
    },
    shouldClearOnSpecialFieldUpdate: false,
  },
  choice: {
    canSubmit: ({ question, answers }) => {
      assertQuestionType(question.type, 'choice')

      const { specialAnswers = [], normalAnswers = [] } = groupAnswers({
        fields: question.fields,
        answers,
      })
      if (hasSelectedChoiceAnswers(specialAnswers)) {
        return true
      }

      const selectedChoicesCount = count(
        (answer) =>
          (answer.type === 'choice' || answer.type === 'choiceText') && answer.value.selected,
        normalAnswers,
      )

      return 1 <= selectedChoicesCount && selectedChoicesCount <= question.maxAnswers
    },
    shouldClearOnSpecialFieldUpdate: true,
  },
  imageChoice: {
    canSubmit: ({ question, answers }) => {
      assertQuestionType(question.type, 'imageChoice')

      const { specialAnswers = [], normalAnswers = [] } = groupAnswers({
        fields: question.fields,
        answers,
      })
      if (hasSelectedChoiceAnswers(specialAnswers)) {
        return true
      }

      const selectedChoicesCount = count(
        (answer) => answer.type === 'choice' && answer.value.selected,
        normalAnswers,
      )

      return 1 <= selectedChoicesCount && selectedChoicesCount <= question.maxAnswers
    },
    shouldClearOnSpecialFieldUpdate: true,
  },
  nps: {
    canSubmit: ({ question, answers }) => {
      assertQuestionType(question.type, 'nps')
      const { specialAnswers = [], normalAnswers = [] } = groupAnswers({
        fields: question.fields,
        answers,
      })
      if (hasSelectedChoiceAnswers(specialAnswers)) {
        return true
      }

      const [npsAnswer] = normalAnswers
      if (npsAnswer?.type !== 'nps') {
        throw new TypeError("Expected answer to be of type 'nps'")
      }
      return typeof npsAnswer?.value.rating === 'number'
    },
    shouldClearOnSpecialFieldUpdate: true,
  },
  slider: {
    /**
     * The Next-disabled-until-moved rule. The handle renders at the centre from the start, but the
     * stored value stays undefined until the respondent moves it -- so "has a number" is exactly
     * "has been touched", with no separate touched flag needed.
     */
    canSubmit: ({ question, answers }) => {
      assertQuestionType(question.type, 'slider')
      const { specialAnswers = [], normalAnswers = [] } = groupAnswers({
        fields: question.fields,
        answers,
      })
      if (hasSelectedChoiceAnswers(specialAnswers)) {
        return true
      }

      const [sliderAnswer] = normalAnswers
      if (sliderAnswer?.type !== 'slider') {
        throw new TypeError("Expected answer to be of type 'slider'")
      }
      return typeof sliderAnswer?.value.value === 'number'
    },
    shouldClearOnSpecialFieldUpdate: true,
  },
  ranked: {
    canSubmit: ({ question, answers }) => {
      assertQuestionType(question.type, 'ranked')
      const { specialAnswers = [], normalAnswers = [] } = groupAnswers({
        fields: question.fields,
        answers,
      })
      if (hasSelectedChoiceAnswers(specialAnswers)) {
        return true
      }
      return normalAnswers.some((answer) => answer.type === 'ranked' && answer.value.ranking !== 0)
    },
    shouldClearOnSpecialFieldUpdate: false,
  },
  gaborGranger: {
    canSubmit: ({ question, answers }) => {
      assertQuestionType(question.type, 'gaborGranger')
      const { normalAnswers = [] } = groupAnswers({ fields: question.fields, answers })

      return normalAnswers.some((answer) => answer.type === 'choice' && answer.value.selected)
    },
    shouldClearOnSpecialFieldUpdate: false,
  },
  maxDiffSet: {
    canSubmit: ({ answers, question }) => {
      const { specialAnswers = [], normalAnswers = [] } = groupAnswers({
        fields: question.fields,
        answers,
      })

      if (hasSelectedChoiceAnswers(specialAnswers)) {
        return true
      }

      return (
        normalAnswers.some(
          (answer) => answer.type === 'maxDiffSet' && answer.value.selectedForBest,
        ) &&
        normalAnswers.some(
          (answer) => answer.type === 'maxDiffSet' && answer.value.selectedForWorst,
        )
      )
    },
    shouldClearOnSpecialFieldUpdate: true,
  },
} as const satisfies Record<
  QuestionType,
  {
    canSubmit: (payload: {
      question:
        | {
            fields: Question['fields']
            type: Exclude<Question['type'], 'choice' | 'imageChoice'>
          }
        | {
            fields: Question['fields']
            type: 'choice'
            maxAnswers: Question<'choice'>['maxAnswers']
          }
        | {
            fields: Question['fields']
            type: 'imageChoice'
            maxAnswers: Question<'imageChoice'>['maxAnswers']
          }
      answers: Answer[]
    }) => boolean
    shouldClearOnSpecialFieldUpdate: boolean
  }
>

const groupAnswers = ({
  fields,
  answers,
}: {
  fields: DeepReadonly<Field[]>
  answers: Answer[]
}): {
  specialAnswers?: Answer[]
  normalAnswers?: Answer[]
} => {
  const idtoField = new Map(fields.map((field) => [field.id, field]))
  return groupBy((answer) => {
    const field = idtoField.get(answer.fieldId)
    if (field === undefined) {
      throw new Error(
        `No field corresponding to answer id?? crazy. id=${answer.fieldId} all ids=${[
          ...idtoField,
        ]}`,
      )
    }
    return field.type === 'special' ? 'specialAnswers' : 'normalAnswers'
  }, answers)
}

const hasSelectedChoiceAnswers = (choiceAnswers: Answer[]): boolean => {
  return choiceAnswers?.some((answer) => {
    if (answer.type !== 'choice' || answer.id === 'other') {
      return false
    }
    return answer.value.selected
  })
}
